import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, aboutStats, aboutMilestones } from '@/mocks/siteData';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '麻豆在线',
        url: 'https://www.madouzx.com',
        description: siteInfo.description,
        email: siteInfo.email,
        telephone: siteInfo.phone,
        address: {
          '@type': 'PostalAddress',
          addressLocality: '北京',
          addressRegion: '朝阳区',
          streetAddress: '光华路数字内容产业园',
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'AboutPage',
        name: '关于麻豆在线 - 官方正版内容平台',
        description: '了解麻豆在线品牌故事、平台优势与联系方式',
        url: 'https://www.madouzx.com/about',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.madouzx.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title="关于我们 - 麻豆在线 | 官方正版内容平台"
        description="了解麻豆在线品牌故事与发展历程，作为官方正版高清影视内容平台，致力于为用户提供优质观影体验。查看平台优势、团队信息与联系方式。"
        keywords="麻豆在线,关于麻豆在线,麻豆在线官方入口,麻豆在线品牌介绍,麻豆在线联系方式"
        canonicalUrl="https://www.madouzx.com/about"
        schema={aboutSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="relative h-[300px] md:h-[400px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20modern%20office%20lounge%20with%20large%20windows%20and%20natural%20light%2C%20elegant%20interior%20design%2C%20comfortable%20seating%20area%2C%20green%20plants%2C%20soft%20cream%20and%20beige%20tones%2C%20professional%20yet%20inviting%20atmosphere%2C%20cinematic%20lighting&width=1800&height=800&seq=about-hero&orientation=landscape"
            alt="关于麻豆在线"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-3">
              关于麻豆在线
            </h1>
            <p className="text-background-50/80 max-w-xl mx-auto">
              官方正版高清影视内容平台，用科技与内容为用户创造价值
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-16">
            {aboutStats.map((stat) => (
              <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-foreground-600">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                品牌故事
              </h2>
              <div className="space-y-4 text-foreground-700 leading-relaxed">
                <p>
                  <strong>麻豆在线</strong>创立于2018年，是国内领先的正版高清影视内容平台。自成立以来，平台始终秉持"内容为王、用户至上"的核心理念，致力于为用户提供高品质、多元化的影视内容服务。
                </p>
                <p>
                  经过多年发展，<strong>麻豆在线</strong>已建立起覆盖电影、电视剧、纪录片、综艺、动漫等全品类的内容矩阵，与超过500家版权方建立深度合作关系，平台累计正版内容超过50,000部。
                </p>
                <p>
                  在技术层面，<strong>麻豆在线高清</strong>技术持续领先行业，支持4K超清画质、杜比全景声、AI智能推荐等前沿功能，为数千万用户带来极致的观影体验。
                </p>
              </div>
            </div>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                平台优势
              </h2>
              <div className="space-y-4">
                {[
                  { icon: 'ri-shield-check-line', title: '正版保障', desc: '所有内容均获正版授权，为用户提供安全合法的观影环境' },
                  { icon: 'ri-4k-line', title: '超清画质', desc: '支持4K超清画质与杜比全景声，带来影院级视听享受' },
                  { icon: 'ri-flashlight-line', title: '极速加载', desc: '智能CDN加速与播放优化技术，确保流畅不卡顿' },
                  { icon: 'ri-device-line', title: '多端同步', desc: '支持手机、平板、电脑、电视等多设备无缝切换' },
                  { icon: 'ri-vip-crown-line', title: '会员尊享', desc: '多层次会员体系，提供独家内容与专属权益' },
                  { icon: 'ri-customer-service-2-line', title: '优质服务', desc: '7×12小时在线客服，快速响应用户需求' },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-accent-600 text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {aboutMilestones.map((milestone, index) => (
                  <div key={milestone.year} className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="hidden md:block flex-1"></div>
                    <div className="relative z-10 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <div className="w-3 h-3 rounded-full bg-background-50"></div>
                    </div>
                    <div className="flex-1 bg-background-100 rounded-xl p-5 border border-background-200/70">
                      <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-bold rounded-full mb-2">
                        {milestone.year}
                      </span>
                      <h3 className="font-heading font-semibold text-foreground-900 mb-1">{milestone.title}</h3>
                      <p className="text-sm text-foreground-600">{milestone.description}</p>
                    </div>
                    {index % 2 !== 0 && <div className="hidden md:block flex-1"></div>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="text-center p-8 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">电子邮箱</h3>
                <p className="text-sm text-foreground-600">{siteInfo.email}</p>
              </div>
              <div className="text-center p-8 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-phone-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">客服热线</h3>
                <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
              </div>
              <div className="text-center p-8 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-wechat-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">微信公众号</h3>
                <p className="text-sm text-foreground-600">{siteInfo.wechat}</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}